import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin, Users, DollarSign, GraduationCap } from 'lucide-react';
import { format } from 'date-fns';

const formatTime = (time: string) => {
  if (!time) return '';
  try {
    const [h, m] = time.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
  } catch { return time; }
};

interface ProfileTrainingSessionsProps {
  userId: string;
  username: string;
}

export const ProfileTrainingSessions = ({ userId, username }: ProfileTrainingSessionsProps) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const { data, error } = await supabase
          .from('training_sessions')
          .select('*')
          .eq('coach_id', userId)
          .order('date', { ascending: true });

        if (!error && data) {
          setSessions(data);
        }
      } catch (err) {
        console.error('Error fetching training sessions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [userId]);

  if (loading || sessions.length === 0) return null;

  const getSlug = (title: string) =>
    title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const getTypeBadgeClass = (type: string) => {
    const colors: Record<string, string> = {
      group_training: 'bg-blue-600/20 text-blue-500 border-blue-600/30',
      private_lesson: 'bg-purple-600/20 text-purple-500 border-purple-600/30',
      camp: 'bg-green-600/20 text-green-500 border-green-600/30',
      clinic: 'bg-orange-600/20 text-orange-500 border-orange-600/30',
    };
    return colors[type] || 'bg-muted text-muted-foreground';
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="text-lg text-foreground flex items-center gap-2">
          <GraduationCap className="h-5 w-5 text-sports-green" />
          Training Sessions ({sessions.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sessions.map((session) => (
            <Card
              key={session.id}
              className="bg-muted/50 border-border hover:border-sports-green/50 transition-colors cursor-pointer"
              onClick={() => navigate(`/${username}/training-sessions/${getSlug(session.title)}`)}
            >
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="font-semibold text-foreground">{session.title}</h4>
                  <Badge className={`${getTypeBadgeClass(session.session_type)} text-xs shrink-0`}>
                    {session.session_type?.replace(/_/g, ' ')}
                  </Badge>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="text-sports-green border-sports-green text-xs">
                    {session.sport}
                  </Badge>
                  {session.skill_level && (
                    <Badge variant="outline" className="text-xs">{session.skill_level}</Badge>
                  )}
                </div>

                <div className="space-y-1 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-3.5 w-3.5 text-sports-green" />
                    <span>{format(new Date(session.date), 'MMM dd, yyyy')}</span>
                  </div>
                  <div className="flex items-center gap-2 font-semibold text-foreground">
                    <span>{formatTime(session.start_time)}</span>
                  </div>
                  {session.location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3.5 w-3.5 text-sports-green" />
                      <span className="truncate">{session.location}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-3.5 w-3.5 text-sports-green" />
                      <span>{session.current_participants || 0}/{session.max_participants || '∞'}</span>
                    </div>
                    <div className="flex items-center gap-1 font-semibold text-foreground">
                      <DollarSign className="h-3.5 w-3.5 text-sports-green" />
                      {session.price}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
